import { z } from "zod";
import {
  normalizeApiBaseUrl,
  parseErrorResponse,
  SharedApiError,
  type FetchLike,
} from "./shared";

export const minVersionResponseSchema = z.object({
  minDesktopVersion: z.string(),
  minIosVersion: z.string(),
  /**
   * Minimum supported `@ano-chat/cli` version. Clients running below
   * this should prompt the user to upgrade. `"0.0.0"` is the no-op
   * default — apps treat it as "no minimum enforced".
   */
  minCliVersion: z.string().default("0.0.0"),
  /**
   * Human-readable upgrade command shown to the user when their CLI is
   * below `minCliVersion`. Typically `"npm install -g @ano-chat/cli@latest"`.
   */
  cliUpgradeCommand: z.string().optional(),
  /**
   * Minimum supported `@ano-chat/skills` Claude Code plugin version.
   * Clients below this prompt the user to update the plugin. `"0.0.0"`
   * is the no-op default — apps treat it as "no minimum enforced", so
   * the nudge stays dark until an operator sets `MIN_SKILL_VERSION`.
   * The plugin version is detected from the local Claude Code install
   * (`~/.claude/plugins/installed_plugins.json`), not the CLI binary —
   * the skills/commands (`/unread`, ano-messages, …) ship via the plugin
   * marketplace, a separate channel from the `ano` CLI.
   */
  minSkillVersion: z.string().default("0.0.0"),
  /**
   * Command shown to the user to install/update the Ano Skills plugin
   * when below `minSkillVersion`. Mirrors the onboarding install string
   * (`claude plugin marketplace add … && claude plugin install …`),
   * which is idempotent and pulls the latest tag — so the same command
   * covers both a missing plugin and a stale one.
   */
  skillUpgradeCommand: z.string().optional(),
  message: z.string().optional(),
  /**
   * Global kill-switch for the Agent Status feature. When false, the
   * three agent_session MCP ops return 404 and the desktop hides the
   * "Agent Status" tab in workspace settings. Defaults to true for
   * forward-compat (apps treat a missing field as enabled).
   */
  agentStatusEnabled: z.boolean().default(true),
  /**
   * The deployed web app base URL for this environment (the server's
   * `appBaseUrl` — e.g. `https://app.ano.dev`, `https://app-staging.ano.dev`,
   * or `http://localhost:1420` in dev). Clients that need to build a
   * web/in-app deep-link (e.g. the CLI's "jump to message" URL for the
   * unread-triage workflow) read this instead of hardcoding an origin.
   * Optional for forward-compat: an older server omits it, and clients
   * fall back to deriving it from their API endpoint.
   */
  webAppUrl: z.string().optional(),
});

export type MinVersionResponse = z.infer<typeof minVersionResponseSchema>;

export function minVersionPath(): string {
  return "/api/min-version";
}

type FetchMinVersionInput = {
  apiBaseUrl: string;
  fetchImpl?: FetchLike;
};

export async function fetchMinVersion({
  apiBaseUrl,
  fetchImpl,
}: FetchMinVersionInput): Promise<MinVersionResponse> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${minVersionPath()}`,
    { method: "GET", headers: { Accept: "application/json" } },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to fetch min version");
  }

  const payload = await response.json().catch(() => null);
  const parsed = minVersionResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid min-version response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}
